import React, { useState, useEffect, useRef } from 'react';
import { login, loginWithGoogle } from '../services/api';

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '274222214444-8olbaa0i4lt5tm2b4imkvp9p1u9ottkg.apps.googleusercontent.com';

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLocal, setShowLocal] = useState(false);
  const googleBtnRef = useRef(null);

  // Render the Google Identity Services button once the GIS script loads.
  useEffect(() => {
    let cancelled = false;

    const init = () => {
      if (cancelled) return;
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        return false;
      }
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
          ux_mode: 'popup',
          auto_select: false,
        });
        if (googleBtnRef.current) {
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            type: 'standard',
            theme: 'filled_blue',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 320,
          });
        }
      } catch (err) {
        console.error('Google Sign-In init failed:', err);
        setError('Google Sign-In failed to initialise. Try the local login fallback below.');
      }
      return true;
    };

    if (!init()) {
      const interval = setInterval(() => {
        if (init()) clearInterval(interval);
      }, 200);
      const timeout = setTimeout(() => clearInterval(interval), 10000);
      return () => { cancelled = true; clearInterval(interval); clearTimeout(timeout); };
    }
    return () => { cancelled = true; };
  }, []);

  const handleGoogleCredential = async (response) => {
    setError(null);
    setLoading(true);
    try {
      const data = await loginWithGoogle(response.credential);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await login(username, password);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="app-logo-icon" style={{ width: 48, height: 48, fontSize: 24 }}>G</div>
          <h2>Gift Award Matcher</h2>
          <p>Sign in with your Google account to continue</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <div ref={googleBtnRef} aria-label="Sign in with Google" />
          {loading && <div role="status" aria-live="polite" style={{ fontSize: 12, color: 'var(--text-muted)' }}>Signing in…</div>}
        </div>

        {error && (
          <div id="login-error" className="login-error" role="alert" style={{ marginTop: 14 }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: 24, textAlign: 'center', fontSize: 12 }}>
          {!showLocal ? (
            <button
              type="button"
              onClick={() => setShowLocal(true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                fontSize: 12,
              }}
            >
              Use local login (backup)
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowLocal(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                fontSize: 12,
              }}
            >
              Hide local login
            </button>
          )}
        </div>

        {showLocal && (
          <form onSubmit={handleSubmit} className="login-form" noValidate style={{ marginTop: 16 }}>
            <div className="login-field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Enter username"
                autoComplete="username"
                aria-required="true"
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>
            <div className="login-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                aria-required="true"
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>
            <button type="submit" className="submit-btn" style={{ width: '100%' }} disabled={loading || !username || !password} aria-busy={loading}>
              {loading ? 'Signing in…' : 'Sign In (local)'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
